import { PageDividerProps } from '@st/document-viewer-kit'
import { DocumentSplit } from '@st/sdk'
import { CloseIcon } from '@st/theme'
import { clsx } from 'clsx'
import { ReactNode } from 'react'

type Props = PageDividerProps & {
  documentSplits: DocumentSplit[]
  onAddSplit: (afterPageNumber: number) => void
  onRemoveSplit: (afterPageNumber: number) => void
}

/**
 * Toolbar displayed above each document page in the StanfordTax Document Viewer.
 * See {@link STPDFDocViewer} for more details.
 *
 * The toolbar shows the current page number and, when `showPageOperations` is enabled,
 * provides a context menu to delete/restore a page.
 */
export function SplitDocumentPageDivider({
  documentSplits,
  pageAbove,
  pageBelow,
  onAddSplit,
  onRemoveSplit
}: Props) {
  const isSplit =
    pageAbove &&
    pageBelow &&
    documentSplits.some((split) => split.pageRange.last === pageAbove.pageNumber)

  if (isSplit) {
    return (
      <div className="group relative flex h-full w-full items-center">
        <div className="h-px w-full border-b border-solid border-white" />

        <DividerButton
          icon={<CloseIcon className="size-4 text-gray-800" />}
          children="Remove split"
          className="invisible absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 group-hover:visible"
          onClick={() => onRemoveSplit(pageAbove.pageNumber)}
        />
      </div>
    )
  } else if (pageAbove) {
    return (
      <div className="group relative flex h-full w-full items-center">
        <div className="invisible h-px w-full border-b border-dashed border-white/80 group-hover:visible" />

        <DividerButton
          icon={<CutIcon className="size-4 text-gray-800" />}
          children="Split"
          className="invisible absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 group-hover:visible"
          onClick={() => onAddSplit(pageAbove.pageNumber)}
        />
      </div>
    )
  } else {
    return null
  }
}

function DividerButton({
  icon,
  children,
  className,
  onClick
}: {
  icon: ReactNode
  children: ReactNode
  className: string
  onClick: () => void
}) {
  return (
    <button
      className={clsx(
        'flex flex-row items-center gap-1 rounded-lg bg-gray-200 px-2 py-0.5',
        className
      )}
      onClick={onClick}
    >
      {icon}
      <div className="text-xs text-gray-700">{children}</div>
    </button>
  )
}

function CutIcon({ className }: { className: string }) {
  return (
    <svg className={className} viewBox="0 0 17 16" fill="none">
      <path
        d="M13.653 13.8975L8.78134 9.02567L6.70184 11.1052C6.79928 11.2718 6.87106 11.4475 6.91717 11.6322C6.96328 11.8167 6.98634 12.0077 6.98634 12.2052C6.98634 12.8116 6.77056 13.3307 6.33901 13.7625C5.90745 14.1944 5.38867 14.4103 4.78267 14.4103C4.17667 14.4103 3.65739 14.1946 3.22484 13.763C2.79239 13.3313 2.57617 12.8125 2.57617 12.2065C2.57617 11.6005 2.79206 11.0813 3.22384 10.6488C3.65573 10.2163 4.17489 10 4.78134 10C4.97878 10 5.16978 10.0231 5.35434 10.0693C5.53901 10.1154 5.71467 10.1872 5.88134 10.2847L7.96084 8.20517L5.88134 6.12567C5.71467 6.22311 5.53901 6.29489 5.35434 6.341C5.16978 6.38722 4.97878 6.41033 4.78134 6.41033C4.17489 6.41033 3.65573 6.19455 3.22384 5.763C2.79206 5.33133 2.57617 4.8125 2.57617 4.2065C2.57617 3.6005 2.79195 3.08128 3.22351 2.64883C3.65506 2.21628 4.17389 2 4.78001 2C5.38601 2 5.90523 2.21594 6.33767 2.64783C6.77012 3.07961 6.98634 3.59872 6.98634 4.20517C6.98634 4.40261 6.96328 4.59361 6.91717 4.77817C6.87106 4.96283 6.79928 5.1385 6.70184 5.30517L14.8838 13.4872V13.8975H13.653ZM10.4737 7.33333L9.65301 6.51283L13.653 2.51283H14.8838V2.92317L10.4737 7.33333ZM4.78134 5.74367C5.20434 5.74367 5.56651 5.593 5.86784 5.29167C6.16906 4.99044 6.31967 4.62828 6.31967 4.20517C6.31967 3.78206 6.16906 3.41989 5.86784 3.11867C5.56651 2.81733 5.20434 2.66667 4.78134 2.66667C4.35823 2.66667 3.99601 2.81733 3.69467 3.11867C3.39345 3.41989 3.24284 3.78206 3.24284 4.20517C3.24284 4.62828 3.39345 4.99044 3.69467 5.29167C3.99601 5.593 4.35823 5.74367 4.78134 5.74367ZM4.78134 13.7437C5.20434 13.7437 5.56651 13.593 5.86784 13.2917C6.16906 12.9904 6.31967 12.6283 6.31967 12.2052C6.31967 11.7821 6.16906 11.4199 5.86784 11.1187C5.56651 10.8173 5.20434 10.6667 4.78134 10.6667C4.35823 10.6667 3.99601 10.8173 3.69467 11.1187C3.39345 11.4199 3.24284 11.7821 3.24284 12.2052C3.24284 12.6283 3.39345 12.9904 3.69467 13.2917C3.99601 13.593 4.35823 13.7437 4.78134 13.7437Z"
        fill="currentColor"
      />
    </svg>
  )
}
